// Chat Component avec intégration DynamoDB
// 
// 🗄️ ARCHITECTURE :
// - DynamoDB : Base de données principale (AWS)
// - Socket.io : Communication temps réel
// - API REST : Chargement des messages existants
// - Redis : Fallback en cas d'erreur DynamoDB
//
// 🔗 ENDPOINTS :
// - CHAT_ENDPOINT : http://localhost:3333 (HTTP)
// - CHAT_WS_ENDPOINT : ws://localhost:3333 (WebSocket)
// - CHAT_API_ENDPOINT : http://localhost:3333/api/messages (API REST)
//
// 📊 FONCTIONNALITÉS :
// - ✅ Chargement des messages existants depuis DynamoDB
// - ✅ Envoi de messages en temps réel via Socket.io
// - ✅ Sauvegarde automatique dans DynamoDB
// - ✅ Indicateur de statut DynamoDB
// - ✅ Fallback vers Redis en cas d'erreur

import React, { useState, useEffect, useRef, useCallback } from "react";
import styled, { ThemeConsumer } from "styled-components";
import { ReactComponent as CollapseIcon } from "../img/icons/collapse.svg";
import io from "socket.io-client";
import { ReactComponent as SettingsIcon } from "../img/icons/settings.svg";
import { useDetectClickOutside } from "react-detect-click-outside";
import { useSelector } from "react-redux";
import { BrowserView } from "react-device-detect";
import { Picker } from "emoji-mart";
import { ReactComponent as Emoji } from "../img/icons/emoji.svg";
import { useTheme } from "styled-components";
import { useParams } from "react-router-dom"
import "../sass/vendors/emoji-mart.css";
import settingsIcon from "../img/icons/settings.svg";
import { InputChat } from "./Input";
import DropDown from "./Dropdown";
import Button from "./Button";
import { ENV_CONFIG } from "../config/environment-unified";

const Chat = styled.aside`
  height: 100%;
  max-width: 300px;
  width: 300px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  background-color: ${({ theme }) => theme.color.secondaryBg};
  padding: 9px 5px;
  position: relative;
`;

const DynamoDBStatus = styled.div`
  position: absolute;
  top: 5px;
  right: 5px;
  background: ${({ isConnected }) => isConnected ? '#28a745' : '#dc3545'};
  color: white;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: bold;
  z-index: 10;
`;

const Header = styled.header`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 7px;
  margin-bottom: 7px;
`;

const Body = styled.div`
  min-height: 60vh;
  max-height: calc(100vh - 120px);
  background: ${({ theme }) => theme.color.background};
  height: 100%;
  border-radius: 10px;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  padding: 10px;
  gap: 8px;
  /* Allow emoji picker popover to overflow outside input area */
  overflow: visible;
`;

const RightSide = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  gap: 12.64px;
`;

const Text = styled.span`
  font-size: 12px;
  display: flex;
  flex-wrap: wrap;
  font-family: ${({ tag }) => (tag ? "Poppins Bold" : "Poppins Medium")};
  color: ${(props, theme) => (props.tag ? "#EE4C50" : ThemeConsumer.text)};
`;

const Messages = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 5px;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.color.border};
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.color.text};
  }
`;

const ChatInputContainer = styled.div`
  width: 100%;
  flex-shrink: 0;
  position: relative;
  margin-top: auto;
`;

const Message = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  word-break: break-all;
`;

const Sender = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 6px;
`;

const SenderAvatar = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${({ theme }) => theme.color.secondaryBg};
  color: ${({ theme }) => theme.color.primaryText};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  text-transform: uppercase;
`;

const Name = styled.a`
  color: ${({ theme }) => theme.color.primary};
  font-size: 12px;
  cursor: pointer;
  display: flex;
  font-family: "Poppins Bold";
  white-space: nowrap;
`;

const Icon = styled(Button)`
  ${({ isCollapsed }) =>
    isCollapsed &&
    `
    transition: transform 200ms ease;
    transform: translateX(-50px);
    border-radius: 5px 0px 0px 5px;
    background-color: rgba(0,0,0,0.3);
    width: auto;
    height: auto;
    padding: 7px 10px;
    svg {
      :hover{
        transform: scale(1.1);
      }
      path{
        fill: white;
      }
    }
  `};
`;

const EmojiContainer = styled.div`
  position: relative;
  width: 100%;
  display: ${({ isLoggedIn }) => (isLoggedIn ? "block" : "none")};
  & > * {
    position: absolute;
    right: 0;
    bottom: calc(100% + 8px);
    z-index: 50;
  }
`;

export default function Chat_(props) {
  const op1 = [0, "Account", "link", settingsIcon];
  const op2 = [0, "Dark Mode", "link", settingsIcon];
  const op3 = [0, "Settings", "link", settingsIcon];
  const ddoptions = [op1, op2, op3];
  const [message, setMessage] = useState("");
  const [showDrop, setShowDrop] = useState(false);
  const msgBox = useRef();
  const [iconClicked, setIconClicked] = useState(false);
  const [dynamoDBConnected, setDynamoDBConnected] = useState(false);
  const theme = useTheme();
  const ref = useDetectClickOutside({
    onTriggered: handleClickOut,
    triggerKeys: ["Escape"],
  });

  function handleIconClicked(click) {
    setIconClicked(click);
  }

  function handleClickOut() {
    setIconClicked(false);
  }

  const { username } = useParams();
  const [messages, setMessages] = useState([]);
  const writeChatRef = useRef();
  const readChatRef = useRef();
  const avatarCacheRef = useRef({});

  // Current user context
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  const usernameLogged = useSelector((state) => state.user.data?.username);
  const currentUserAvatar = useSelector((state) => state.user.data?.avatar);
  const id = username;

  // Fetch avatar by username via stream service (profile/stream endpoint)
  const fetchAvatarFor = useCallback(async (user) => {
    try {
      // Prefer the authenticated user's real avatar for self
      if (user === usernameLogged && currentUserAvatar) {
        avatarCacheRef.current[user] = currentUserAvatar;
        setMessages(curr => curr.slice());
        return;
      }
      const url = (ENV_CONFIG.STREAM_ENDPOINT || '/api/stream/stream/') + encodeURIComponent(user);
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      if (data && data.avatar) {
        avatarCacheRef.current[user] = data.avatar;
        // trigger re-render
        setMessages(curr => curr.slice());
      }
    } catch (e) {
      // silent
    }
  }, [usernameLogged, currentUserAvatar]);

  useEffect(() => {
    setMessages([])
    avatarCacheRef.current = {};
  }, [username])

  useEffect(() => {
    // Force production mode if on corearena.tv domain
    const isProductionDomain = window.location.hostname === 'corearena.tv';
    const isProduction = isProductionDomain || process.env.NODE_ENV === 'production' || process.env.REACT_APP_FORCE_PRODUCTION === 'true';
    
    console.log("🔗 Chat Configuration:");
    console.log("🔧 Environment:", process.env.NODE_ENV);
    console.log("🔧 Hostname:", window.location.hostname);
    console.log("🔧 Is Production Domain:", isProductionDomain);
    console.log("🔧 Mode:", isProduction ? "PRODUCTION" : "DEVELOPMENT");
    
    // Override ENV_CONFIG for production
    let chatEndpoint, wsEndpoint, apiEndpoint;
    
    if (isProduction) {
      chatEndpoint = '';  // Same-origin
      wsEndpoint = '';    // Same-origin
      apiEndpoint = '/api/messages';  // Same-origin API
      console.log("🌐 [PRODUCTION] Using same-origin configuration");
    } else {
      const { CHAT_ENDPOINT, CHAT_WS_ENDPOINT, CHAT_API_ENDPOINT } = ENV_CONFIG;
      chatEndpoint = CHAT_ENDPOINT;
      wsEndpoint = CHAT_WS_ENDPOINT;
      apiEndpoint = CHAT_API_ENDPOINT;
      console.log("🔧 [DEVELOPMENT] Using ENV_CONFIG");
    }
    
    console.log("🔧 Final Chat endpoint:", chatEndpoint);
    console.log("🔧 Final WebSocket endpoint:", wsEndpoint);
    console.log("🔧 Final API endpoint:", apiEndpoint);

    // ✅ Connexion WebSocket avec retry automatique
    const connectWebSocket = () => {
      try {
        // Production: same-origin, no port, important: path '/socket.io'
        const socketOptions = {
          path: '/socket.io',
          transports: ['websocket'],
          withCredentials: true,
          forceNew: true,
          timeout: 10000,
          reconnection: true,
          reconnectionAttempts: 5,
          reconnectionDelay: 1000
        };

        if (isProduction) {
          // Use relative paths for same-origin requests in production
          console.log("🌐 [PRODUCTION] Using same-origin WebSocket with path '/socket.io'");
          writeChatRef.current = io('/writechat', socketOptions);
          readChatRef.current = io('/readchat', socketOptions);
        } else {
          // Development: use full URL
          console.log("🔧 [DEVELOPMENT] Using full URL WebSocket");
          writeChatRef.current = io(chatEndpoint + "/writechat", {
            ...socketOptions,
            transports: ['websocket', 'polling'] // Allow polling fallback in dev
          });
          readChatRef.current = io(chatEndpoint + "/readchat", {
            ...socketOptions,
            transports: ['websocket', 'polling'] // Allow polling fallback in dev
          });
        }

        writeChatRef.current.emit("join", { streamID: username });
        readChatRef.current.emit("join", { streamID: username });

        // Écouter les nouveaux messages en temps réel (avoid duplicates)
        readChatRef.current.on("send", (newMessage) => {
          console.log("📨 Message reçu (DynamoDB):", newMessage);
          
          // Validate incoming message
          if (!newMessage || !newMessage.username || !newMessage.message) {
            console.warn("⚠️ Message invalide ignoré:", newMessage);
            return;
          }
          
          setMessages((curr) => {
            // Check if message already exists to prevent duplicates
            const messageExists = curr.some(msg => 
              msg.message === newMessage.message && 
              msg.username === newMessage.username && 
              Math.abs(new Date(msg.timestamp) - new Date(newMessage.timestamp)) < 1000
            );
            
            if (messageExists) {
              console.log("⚠️ Message duplicate ignoré:", newMessage.message);
              return curr;
            }
            
            // Ensure message has safe properties
            const safeMessage = {
              username: newMessage.username || 'Anonymous',
              message: newMessage.message || '',
              streamID: newMessage.streamID || username,
              timestamp: newMessage.timestamp || new Date().toISOString(),
              avatar: newMessage.avatar || null
            };
            
            return [...curr, safeMessage];
          });

          // Lazy-load sender avatar if missing
          const sender = newMessage.username;
          if (sender && !avatarCacheRef.current[sender]) {
            fetchAvatarFor(sender);
          }
        });

        // ✅ Gestion améliorée des erreurs de connexion avec retry
        writeChatRef.current.on("connect_error", (error) => {
          console.error("❌ Erreur connexion writechat:", error);
          setDynamoDBConnected(false);

          // Tentative de reconnexion automatique avec backoff
          setTimeout(() => {
            console.log("🔄 Tentative de reconnexion writechat...");
            if (writeChatRef.current) {
              writeChatRef.current.connect();
            }
          }, 3000);
        });

        readChatRef.current.on("connect_error", (error) => {
          console.error("❌ Erreur connexion readchat:", error);
          setDynamoDBConnected(false);

          // Tentative de reconnexion automatique avec backoff
          setTimeout(() => {
            console.log("🔄 Tentative de reconnexion readchat...");
            if (readChatRef.current) {
              readChatRef.current.connect();
            }
          }, 3000);
        });

        // ✅ Gestion des déconnexions
        writeChatRef.current.on("disconnect", (reason) => {
          console.log("📴 Writechat déconnecté:", reason);
          setDynamoDBConnected(false);
          if (reason === "io server disconnect") {
            // Déconnexion forcée par le serveur
            setTimeout(() => {
              if (writeChatRef.current) {
                writeChatRef.current.connect();
              }
            }, 1000);
          }
        });

        readChatRef.current.on("disconnect", (reason) => {
          console.log("📴 Readchat déconnecté:", reason);
          setDynamoDBConnected(false);
          if (reason === "io server disconnect") {
            // Déconnexion forcée par le serveur
            setTimeout(() => {
              if (readChatRef.current) {
                readChatRef.current.connect();
              }
            }, 1000);
          }
        });

        // Log de connexion réussie
        writeChatRef.current.on("connect", () => {
          console.log(`✅ Connexion writechat établie (${isProduction ? 'PROD' : 'DEV'})`);
          setDynamoDBConnected(true);
        });

        readChatRef.current.on("connect", () => {
          console.log(`✅ Connexion readchat établie (${isProduction ? 'PROD' : 'DEV'})`);
          setDynamoDBConnected(true);
        });

      } catch (error) {
        console.error("❌ Erreur lors de la connexion WebSocket:", error);
        setDynamoDBConnected(false);
      }
    };

    // Initialiser la connexion
    connectWebSocket();

    // Charger les messages existants depuis DynamoDB
    const loadExistingMessages = async () => {
      try {
        const response = await fetch(`${apiEndpoint}/${username}?limit=50`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          setDynamoDBConnected(true);
          if (data.items && data.items.length > 0) {
            console.log("📨 Messages chargés depuis DynamoDB:", data.items.length);
            // Convertir le format DynamoDB vers le format attendu avec validation
            const formattedMessages = data.items
              .filter(item => item && item.username && item.content) // Filter out invalid items
              .map(item => ({
                username: item.username || 'Anonymous',
                message: item.content || '',
                streamID: item.streamId || username,
                timestamp: item.timestamp || new Date().toISOString(),
                avatar: item.avatar || null
              }));
            console.log("📨 Messages validés:", formattedMessages.length, "sur", data.items.length);
            setMessages(formattedMessages);

            // Prefetch avatars for unique usernames
            const uniqueUsers = Array.from(new Set(formattedMessages.map(m => m.username).filter(Boolean)));
            uniqueUsers.forEach(u => {
              if (!avatarCacheRef.current[u]) fetchAvatarFor(u);
            });
          }
        } else {
          console.error("❌ Erreur lors du chargement des messages:", response.status);
          setDynamoDBConnected(false);
        }
      } catch (error) {
        console.error("❌ Erreur lors du chargement des messages:", error);
        setDynamoDBConnected(false);
      }
    };

    // Charger les messages existants
    loadExistingMessages();

    return () => {
      // Cleanup WebSocket connections
      console.log("🧹 Cleaning up chat connections...");
      if (writeChatRef.current) {
        writeChatRef.current.removeAllListeners();
        writeChatRef.current.disconnect();
        writeChatRef.current = null;
      }
      if (readChatRef.current) {
        readChatRef.current.removeAllListeners();
        readChatRef.current.disconnect();
        readChatRef.current = null;
      }
    };
  }, [username, fetchAvatarFor]);

  // const isProduction = process.env.NODE_ENV === 'production';

  // Keep self avatar cached for correct rendering of own messages
  useEffect(() => {
    if (usernameLogged && currentUserAvatar) {
      avatarCacheRef.current[usernameLogged] = currentUserAvatar;
    }
  }, [usernameLogged, currentUserAvatar]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (msgBox.current) {
      msgBox.current.scrollTo(
        0,
        msgBox.current.scrollHeight - msgBox.current.clientHeight
      );
    }
  }, [messages, currentUserAvatar]);

  // Handle message sending with Enter key (only set up once)
  useEffect(() => {
    function checkMessage(msg) {
      if (!msg.replace(/\s/g, "").length) {
        return "";
      }
      return msg;
    }

    const listener = (event) => {
      if (
        event.code === "Enter" ||
        event.keyCode === 13 ||
        event.code === "NumpadEnter"
      ) {
        const currentMessage = event.target.value || "";
        if (checkMessage(currentMessage) !== "" && writeChatRef.current && writeChatRef.current.connected) {
          const isProduction = true; // Force PRODUCTION pour test
          console.log(`📤 Envoi du message (${isProduction ? 'PROD' : 'DEV'})`);
          writeChatRef.current.emit("message", {
            message: currentMessage,
            username: usernameLogged,
            streamID: id,
            avatar: currentUserAvatar || null
          });
          event.preventDefault();
          setMessage("");
        }
      }
    };

    const chatInput = document.getElementById("chat-input");
    if (chatInput) {
      chatInput.addEventListener("keydown", listener);
      return () => {
        chatInput.removeEventListener("keydown", listener);
      };
    }
  }, [usernameLogged, id, currentUserAvatar]); // Removed message dependency

  return (
    <>
      <Chat {...props} style={(window.location.pathname === "/streamChat/" + username) ? { maxWidth: "100%" } : {}} >
        <DynamoDBStatus isConnected={dynamoDBConnected}>
          {dynamoDBConnected ? "Online" : "Offline"}
        </DynamoDBStatus>
        <Header>
          <BrowserView>
            <Icon
              icon
              isCollapsed={props.isCollapsed}
              onClick={props.handleCollapse}
            >
              <CollapseIcon />
            </Icon>
          </BrowserView>
          <h6>Stream Chat</h6>
          <RightSide>
            <Button
              icon
              onClick={() => setShowDrop((curr) => !curr)}
              style={{ position: "relative" }}
              settings
              width={"15px"}
            >
              <SettingsIcon />
              {showDrop ? (
                <DropDown
                  style={{ top: "100%", right: "0%" }}
                  options={ddoptions}
                  margin={"10px"}
                />
              ) : (
                ""
              )}
            </Button>
          </RightSide>
        </Header>
        <Body>
          <Messages ref={msgBox}>
            {messages
              ? messages
                .filter(msg => msg && msg.username && msg.message) // Additional safety filter
                .map((msg, index) => {
                  const safeUsername = msg.username || 'Anonymous';
                  const safeMessage = msg.message || '';
                  
                  const initial = (safeUsername || 'U').trim().charAt(0);
                  // Prefer avatar included in message payload; fallback to cache/self/avatar
                  const payloadAvatar = msg.avatar;
                  const cachedAvatar = avatarCacheRef.current[safeUsername];
                  const avatarUrl = payloadAvatar || (safeUsername === usernameLogged
                    ? (currentUserAvatar || cachedAvatar)
                    : cachedAvatar);
                  return (
                    <Message key={index}>
                      <Sender>
                        {avatarUrl ? (
                          <img src={avatarUrl} alt={safeUsername} style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover' }} />
                        ) : (
                          <SenderAvatar>{initial}</SenderAvatar>
                        )}
                        <Name href={"/".concat(safeUsername)}>{safeUsername}</Name>
                        <span style={{ color: '#888', margin: '0 4px' }}>:</span>
                      </Sender>
                      {safeMessage.split(" ").map((word, index) => {
                        if (word[0] === "@")
                          return (
                            <Text
                              tag={true}
                              key={index}
                              style={{ color: "#EE4C50" }}
                            >
                              {word}&nbsp;
                            </Text>
                          );
                        else return <Text key={index}>{word}&nbsp;</Text>;
                      })}
                    </Message>
                  );
                })
              : ""}
          </Messages>
          <ChatInputContainer ref={ref}>
            <EmojiContainer isLoggedIn={isLoggedIn}>
              {iconClicked ? (
                <Picker
                  theme={theme.mode === "light" ? "light" : "dark"}
                  emojiTooltip={false}
                  showPreview={false}
                  onSelect={(emoji) => {
                    setMessage((curr) => curr + emoji.native + " ");
                  }}
                  set="twitter"
                />
              ) : (
                ""
              )}
            </EmojiContainer>
            <form
              autocomplete="off"
              onSubmit={(e) => {
                e.preventDefault();
              }}
              style={{ width: "100%" }}
            >
              <InputChat
                disabled={!isLoggedIn}
                iconClicked={handleIconClicked}
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                }}
                iconRight={Emoji}
                id="chat-input"
                placeholder={isLoggedIn ? "Send a message " : "You need to login"}
                type="text"
              />
            </form>
          </ChatInputContainer>
        </Body>
      </Chat>
    </>
  );
}
